import "dayjs/locale/vi"

import { Link } from "@tanstack/react-router"
import dayjs from "dayjs"
import localizedFormat from "dayjs/plugin/localizedFormat"
import relativeTime from "dayjs/plugin/relativeTime"
import { SearchNormal1 } from "iconsax-reactjs"
import React from "react"
import { Controller, useForm } from "react-hook-form"
import InfiniteScroll from "react-infinite-scroll-component"
import PullToRefresh from "react-simple-pull-to-refresh"

import { getAllBlogs } from "@/api"
import { GoBack, LoadingSpinner } from "@/components/shared"
import { Image } from "@/components/ui"
import { useInfiniteScrollQuery } from "@/modules/helpers/hooks"
import { useDebounce } from "@/modules/helpers/hooks/shared/use-debounce"
import type { Blog } from "@/modules/types/others/blog"

dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)
dayjs.locale("vi")

const SKELETON_COUNT = 6

const EmptyState: React.FC = () => (
  <div className="h-36 flex justify-center w-full items-center">
    <p className="text-xs text-gray-400">Chưa có bài viết</p>
  </div>
)

const BlogCardSkeleton: React.FC = () => (
  <div className="bg-gray-50 rounded-lg overflow-hidden animate-pulse p-3">
    <div className="flex gap-3">
      <div className="w-20 h-20 bg-gray-200 rounded flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-4/5" />
        <div className="h-3 bg-gray-200 rounded w-full" />
        <div className="h-3 bg-gray-200 rounded w-2/3" />
        <div className="h-3 bg-gray-200 rounded w-1/3" />
      </div>
    </div>
  </div>
)

const BlogCard: React.FC<{ blog: Blog }> = ({ blog }) => {
  return (
    <Link
      to="/blog/$id"
      params={{ id: String(blog.id) }}
      className="bg-background rounded-lg overflow-hidden p-2 active:bg-gray-100 transition-colors relative"
    >
      <div className="flex gap-3">
        <div className="relative w-26 h-26 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
          <Image src={blog.thumbnail} alt={blog.title} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-primary-800 leading-snug line-clamp-2 mb-1">
            {blog.title}
          </h3>
          <h2 className="text-xs text-gray-400 mt-1">{blog.description}</h2>
          <p className="text-xss text-primary-800 mt-1 absolute bottom-1 right-2">
            {dayjs(blog.createdAt).format("HH:mm, DD/MM/YYYY")}
          </p>
        </div>
      </div>
    </Link>
  )
}

const BlogGrid: React.FC<{
  data: Blog[] | undefined
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  onLoadMore: () => void
}> = ({ data, isLoading, hasNextPage, onLoadMore }) => {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-3">
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <BlogCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (!data?.length) {
    return <EmptyState />
  }

  return (
    <InfiniteScroll
      dataLength={data.length}
      next={onLoadMore}
      hasMore={hasNextPage}
      loader={<LoadingSpinner className="w-16 h-16" />}
      scrollableTarget="scrollable"
    >
      <div className="flex flex-col gap-2 p-3 pb-8">
        {data.map((blog) => (
          <BlogCard key={blog.id} blog={blog} />
        ))}
      </div>
    </InfiniteScroll>
  )
}

interface BlogSearchForm {
  search: string
}

const BlogList = () => {
  const { control, watch } = useForm<BlogSearchForm>({
    defaultValues: {
      search: ""
    }
  })

  const searchValue = watch("search")
  const debouncedSearch = useDebounce(searchValue, 300)

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, forceRefetch } =
    useInfiniteScrollQuery({
      queryKey: ["blog", debouncedSearch],
      queryFn: getAllBlogs,
      params: {
        page: 1,
        take: 50,
        content: debouncedSearch || undefined
      }
    })

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }

  const handleRefresh = async () => {
    await forceRefetch()
  }

  return (
    <>
      {/* Header */}
      <div className="sticky top-0 z-10">
        <GoBack title="Bài viết" className="bg-white" />
      </div>
      {/* Search Bar */}
      <div className="px-3 mt-1">
        <div className="relative">
          <SearchNormal1
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400"
            color="var(--color-primary)"
          />
          <Controller
            name="search"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                placeholder="Tìm kiếm bài viết"
                className="w-full rounded-md border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            )}
          />
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col" id="scrollable">
        <PullToRefresh
          onRefresh={handleRefresh}
          pullingContent={""}
          className="flex-1 h-full"
          maxPullDownDistance={80}
          refreshingContent={<LoadingSpinner className="w-20 h-20" />}
        >
          <BlogGrid
            data={data?.list}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={!!hasNextPage}
            onLoadMore={handleLoadMore}
          />
        </PullToRefresh>
      </div>
    </>
  )
}

export default BlogList
